import { useEffect, type JSX } from 'react';
import { createAuthTokenGetter, setAuthTokenGetter } from '../../api';
import { useAppAuth } from '../../contexts';
import { clearEptSessionCookie, setEptSessionCookie } from '../../utils/crossDomainAuth';
import { pushEptUserToDataLayer } from '../../utils/googleTagManager';
import { cacheManager } from '../../utils/queryClient';
import {
  clearRealmHint,
  realmFromClaims,
  resetRealmRedirect,
  writeRealmHint,
} from '../../utils/realmHint';
import {
  installSessionAuthInterceptors,
  registerSessionAuthHandlers,
} from '../../utils/sessionAuth';

/**
 * Syncs auth state to React Query cache and the GTM data layer.
 * Registers centralized 401 session recovery (refresh-and-replay / re-login).
 */
export function AuthStateMonitor(): JSX.Element {
  const {
    isAuthenticated,
    user,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAppAuth();

  useEffect(() => {
    if (!isLoading) {
      cacheManager.handleAuthStateChange(isAuthenticated, user);
    }
  }, [isAuthenticated, user, isLoading]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      clearEptSessionCookie();
      return;
    }

    setEptSessionCookie();
  }, [isAuthenticated, isLoading]);

  // Remember the signed-in user's SSO so the next visit skips the email form
  // (EPD-79). Signed-out never clears it: that is exactly the visit it serves.
  // Re-running on token refresh keeps the cookie's one-year window rolling.
  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }
    resetRealmRedirect();
    void getIdTokenClaims()
      .then(claims => {
        if (!claims) {
          // Unknown, not "no SSO": the Auth0 wrapper answers null on any
          // error. Keep whatever the last successful sign-in saved.
          return;
        }
        const hint = realmFromClaims(claims);
        if (hint) {
          writeRealmHint(hint);
        } else {
          // Social logins: a hint left by someone else would
          // send this browser to the wrong IdP or account.
          clearRealmHint();
        }
      })
      .catch(() => {
        // Must never affect the signed-in session.
      });
  }, [isAuthenticated, isLoading, getIdTokenClaims]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isAuthenticated && user) {
      const accountId = user.ept_app_metadata?.account_id;
      pushEptUserToDataLayer({
        auth0_user_id: user.sub || null,
        user_email: user.email ?? null,
        account_id: typeof accountId === 'number' ? accountId : null,
      });
    } else {
      pushEptUserToDataLayer({
        auth0_user_id: null,
        user_email: null,
        account_id: null,
      });
    }
  }, [isAuthenticated, user, isLoading]);

  // Token getter for src/api apiRequest (additive; existing fetch sites unchanged).
  useEffect(() => {
    setAuthTokenGetter(createAuthTokenGetter(getIdTokenClaims, getAccessTokenSilently));
    return () => {
      setAuthTokenGetter(null);
    };
  }, [getAccessTokenSilently, getIdTokenClaims]);

  // Centralized 401 handling for both default axios and apiClient.
  useEffect(() => {
    registerSessionAuthHandlers({
      refreshAuthToken: async () => {
        const envToken = process.env.REACT_APP_AUTH0_TOKEN;
        if (envToken) {
          return envToken;
        }

        // Force Auth0 to hit the network so we don't replay with a stale cache.
        await getAccessTokenSilently({ cacheMode: 'off' });
        const claims = await getIdTokenClaims();
        if (claims?.__raw) {
          return claims.__raw;
        }
        return getAccessTokenSilently({ cacheMode: 'off' });
      },
    });

    const eject = installSessionAuthInterceptors();

    return () => {
      eject();
      registerSessionAuthHandlers(null);
    };
  }, [getAccessTokenSilently, getIdTokenClaims]);

  return <></>;
}
